// @flow

import * as React from 'react';

import classNames from '@ta/common.classnames';
import { InteractionLogger } from '@ta/tracking.interactions';
import { Link } from '@ta/common.routing';
import { localize, unescaped } from '@ta/common.i18n';
import { MemberStatsBasic } from '@ta/social.member-common';
import { Avatar, type AvatarPhoto } from '@ta/styleguide.avatar';
import type { MemberHometown as MemberHometownType } from '@ta/social.member-types';

import styles from './MatchingItemsMemberContent.less';

type Route = {|
  /** Url of the route */
  url: string,
|};

type UserProfile = {|
  /** the userId of the profile */
  userId: string,
  /** Display name of the user */
  displayName: string,
  /** Route of the user profile */
  route: Route,
  /** Like the blue twitter check mark except green for tripadvisor */
  isVerified: boolean,
  /** Member's hometown */
  hometown: ?MemberHometownType,
  /** Count of followers */
  followerCount: number,
  /** Total count of contributions */
  contributionCounts: {
    sumAllUgc: number,
  },
  /** the image of the user profile */
  avatar?: AvatarPhoto,
|};

type Props = {
  /** userprofile object to display */
  userProfile: UserProfile,
  /** is it a dmo sponsored profile */
  isSponsored?: boolean,
};

const MatchingItemsMemberContent = ({ userProfile, isSponsored }: Props) => {
  const ProfileAvatar = props => (
    <div className={styles.avatar_container}>
      <Avatar
        photo={userProfile?.avatar}
        display="inline"
        target="_self"
        href={userProfile?.route?.url}
        withBorder
        onClick={e =>
          props.logInteraction(e, 'userClick', {
            userId: userProfile?.userId,
          })
        }
        size="default"
      />
    </div>
  );

  const ByProfileName = props => (
    <div className={styles.display_name_wrapper}>
      {localize('ugc_by_username_v2')} {/* By */}
      <Link
        className={classNames('ui_link', styles.display_name, {
          [styles.verified]: userProfile?.isVerified,
        })}
        href={userProfile?.route?.url}
        onClick={e => props.logInteraction(e, 'userClick', { userId: userProfile?.userId })}
      >
        {userProfile?.displayName}
      </Link>
    </div>
  );
  if (isSponsored) {
    return (
      <InteractionLogger>
        {logInteraction => (
          <div className={styles.name_and_avatar}>
            <ProfileAvatar logInteraction={logInteraction} />
            <div className={styles.text_wrap}>
              <ByProfileName logInteraction={logInteraction} />
              <div className={styles.sponsored}>
                {unescaped(localize('discovery_sponsored_label')) /* Sponsored */}
              </div>
            </div>
          </div>
        )}
      </InteractionLogger>
    );
  }
  return (
    <InteractionLogger>
      {logInteraction => (
        <div className={styles.name_and_avatar}>
          <ProfileAvatar logInteraction={logInteraction} />
          <div className={styles.text_wrap}>
            <ByProfileName logInteraction={logInteraction} />
            <MemberStatsBasic profile={userProfile} isSingleLine hideHometown noBold />
          </div>
        </div>
      )}
    </InteractionLogger>
  );
};

export default MatchingItemsMemberContent;
